import React from "react";
import { NextIntlClientProvider } from "next-intl";
import App from "next/app";
import { Provider } from "react-redux";
import { defaultLocale } from "@lib/i18n/config";
import { wrapper } from "@lib/store";
import Bugsnag from "@lib/bugsnag";
import "@algolia/autocomplete-theme-classic";
import customInitStoryblok from "@lib/storyblok";
import { AxiosInterceptor } from "@lib/axios";
import AppInit from "@lib/AppInit";
import ToastConfig from "@utils/toasts/ToastConfig";
import UserListManager from "@components/views/UserListManager";
import Layout from "@components/layout/Layout";
import Error from "./_error";
import { getFeatures } from "@actions/features-actions";
import "swiper/css";
import "swiper/css/pagination";
import "react-dates/lib/css/_datepicker.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-toastify/dist/ReactToastify.css";

// eslint-disable-next-line import/no-dynamic-require
require(`../styles/index-${process.env.NEXT_PUBLIC_CUSTOMER}.scss`);

customInitStoryblok();

// Must be created outside the component to keep a stable reference across renders.
// If created inside, a new component type is produced on every render, causing React
// to unmount and remount the entire subtree (breaking shallow routing and causing full page reloads).
const bugsnagPlugin = Bugsnag.getPlugin("react");
const ErrorBoundary = bugsnagPlugin?.createErrorBoundary(React) ?? (({ children }) => children);

function MyApp({ Component, ...rest }) {
  const { store, props } = wrapper.useWrappedStore(rest);
  const headerBehaviors = props.pageProps.headerBehaviors || {};
  const initPromoCode = props.pageProps.initPromoCode || false;
  const { locale, messages } = props.pageProps;

  const unauthRequired = !!Component.requireNoAuth;
  const authRequired =
    Component.requireAuth || (!Component.requireNoAuth && Component.requireAuth !== false);

  return (
    <NextIntlClientProvider
      locale={locale || defaultLocale}
      messages={messages || {}}
      onError={() => {}}
      getMessageFallback={({ key }) => key}
    >
      <ErrorBoundary FallbackComponent={Error}>
        <Provider store={store}>
          <AxiosInterceptor>
            <Layout headerBehaviors={headerBehaviors}>
              <AppInit
                unauthRequired={unauthRequired}
                authRequired={authRequired}
                initPromoCode={initPromoCode}
              >
                <Component {...props.pageProps} />
                <ToastConfig />
                <UserListManager />
              </AppInit>
            </Layout>
          </AxiosInterceptor>
        </Provider>
      </ErrorBoundary>
    </NextIntlClientProvider>
  );
}

MyApp.getInitialProps = wrapper.getInitialAppProps((store) => async ({ Component, ctx }) => {
  const areLogsEnabled = ctx?.router?.query?.debug || "";
  global.areLogsEnabled = areLogsEnabled === "true";
  const { host, xproxiedfromwww } = ctx.req?.headers || {};
  if (
    (host === "app.lecloset.fr" || host === "app.legranddressing.com") &&
    xproxiedfromwww === undefined
  ) {
    ctx.res.writeHead(301, {
      Location: `https://${host.replace("app", "www")}${ctx.req.url}`,
    });
    ctx.res.end();
    return null;
  }

  await store.dispatch(getFeatures());

  const appProps = await App.getInitialProps({ Component, ctx });
  return {
    ...appProps,
  };
});

export default MyApp;
